import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Search, Users, Sparkles } from 'lucide-react';

/**
 * Admin > Events: "Manage Attendance" modal.
 * Shows every registered RSVP for an event with an Attended toggle.
 * Attendance unlocks keynote access for the member.
 */
export default function EventAttendanceDialog({ open, onClose, eventId, eventTitle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingFor, setSavingFor] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/events/${eventId}/attendance`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load attendance');
    }
    setLoading(false);
  };

  useEffect(() => { if (open) load(); }, [open, eventId]);

  const toggleOne = async (uid, currentAttended) => {
    setSavingFor(uid);
    try {
      const r = await fetch(`/api/admin/events/${eventId}/attendance/${uid}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attended: !currentAttended }),
      });
      if (!r.ok) throw new Error('Failed');
      // Optimistically update
      setData(prev => prev && ({
        ...prev,
        attended_count: prev.attended_count + (currentAttended ? -1 : 1),
        attendees: prev.attendees.map(a => a.user_id === uid ? { ...a, attended: !currentAttended } : a),
      }));
    } catch (e) {
      toast.error('Could not update — please retry');
    }
    setSavingFor(null);
  };

  const markAll = async () => {
    if (!data) return;
    setBulkSaving(true);
    try {
      const r = await fetch(`/api/admin/events/${eventId}/mark-all-attended`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed');
      const result = await r.json();
      toast.success(
        result.granted > 0
          ? `Granted attendance to ${result.granted} new member${result.granted === 1 ? '' : 's'}`
          : 'All registered members already marked as attended',
      );
      load();
    } catch (e) {
      toast.error('Bulk update failed');
    }
    setBulkSaving(false);
  };

  const filtered = (data?.attendees || []).filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.name || '').toLowerCase().includes(q)
      || (a.email || '').toLowerCase().includes(q)
      || (a.company_name || '').toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="event-attendance-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-brand-sage" />
            Manage Attendance — {eventTitle}
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Confirming a member as <strong>attended</strong> unlocks the Keynotes
            page for them. Use the "Mark all as attended" button after the event,
            then untick any no-shows.
          </p>
        </DialogHeader>

        {loading || !data ? (
          <div className="text-center py-10 text-slate-400">Loading…</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4 mt-3 mb-4 flex-wrap">
              <div className="flex items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1 text-slate-700">
                  <Users className="h-4 w-4 text-slate-500" />
                  <strong data-testid="attendance-registered-count">{data.registered}</strong> registered
                </span>
                <span className="inline-flex items-center gap-1 text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <strong className="text-emerald-700" data-testid="attendance-attended-count">{data.attended_count}</strong> attended
                </span>
                {!data.auto_attendance_eligible && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                    <Clock className="h-3 w-3" />
                    Event hasn't ended yet
                  </span>
                )}
              </div>
              <Button
                onClick={markAll}
                disabled={bulkSaving || data.registered === 0}
                data-testid="mark-all-attended-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                size="sm"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {bulkSaving ? 'Saving…' : 'Mark all as attended'}
              </Button>
            </div>

            <div className="relative mb-3">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, email or company…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="attendance-search"
              />
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  {search ? 'No matches' : 'No registered attendees yet'}
                </div>
              ) : filtered.map((a) => (
                <div
                  key={a.user_id}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50"
                  data-testid={`attendance-row-${a.user_id}`}
                >
                  <Checkbox
                    checked={a.attended}
                    disabled={savingFor === a.user_id}
                    onCheckedChange={() => toggleOne(a.user_id, a.attended)}
                    data-testid={`attendance-toggle-${a.user_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{a.name || '—'}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {a.email}{a.job_title ? ` · ${a.job_title}` : ''}{a.company_name ? ` · ${a.company_name}` : ''}
                    </p>
                  </div>
                  {a.attended && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold whitespace-nowrap">
                      KEYNOTE ACCESS
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-end mt-5 pt-3 border-t border-slate-100">
          <Button variant="ghost" onClick={onClose} data-testid="attendance-close">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
